import { loadStripe } from "@stripe/stripe-js";
import type { StripeConstructorOptions } from "@stripe/stripe-js";
import { ref } from "vue";
import type { Ref } from "vue";
import emitter from "@/eventBus";
import { defineStore } from "pinia";
import { publicIpv4 } from "public-ip";
import { authenticatedCheckout } from "@/graphql/functions/authenticatedCheckout";
import { guestCheckout } from "@/graphql/functions/guestCheckout";
import type { SubmittedManagementQuestionAnswer } from "@/models/checkout/GuestCheckoutRequest";
import { useBookingData } from "@/stores/booking-storage";
import { DeliveryMethods } from "@/models/basket/DeliveryMethods";
import type { StripeError } from "@stripe/stripe-js/dist/stripe-js/stripe";
import { handleError } from "@/services/AlertService";
import { isUserLoggedIn } from "@/utilities/isUserLoggedIn";
import type { RetailInstanceTypeService } from "@/services/RetailInstanceTypeService";
import type { FrontendMiQuestionModel } from "@/models/checkout/FrontendMiQuestionModel";
import type { IBookingData } from "@/models/checkout/FulfilmentRequestModel";

export interface StripeConfiguration {
    publishableKey: string;
    accountId?: string;
}

export const usePaymentStore = defineStore("payment", () => {
    const stripeReady: Ref<boolean> = ref(false);
    const stripePublishableKey: Ref<string> = ref("");
    const stripeAccountId: Ref<string | null> = ref(null);
    // Error message from the Stripe library
    const stripePaymentErrorToDisplay: Ref<StripeError | null> = ref(null);
    const miQuestions = ref({
        questionsAndAnswers: <FrontendMiQuestionModel[]>[],
    });
    const isFinalPaymentStepLoading: Ref<boolean> = ref(false);
    const retailServiceInstance: Ref<RetailInstanceTypeService | null> = ref(null);
    const guestUserFirstName: Ref<string> = ref("");
    const guestUserLastName: Ref<string> = ref("");
    const guestUserEmail: Ref<string> = ref("");

    // Action to set the service instance, so we can use isCorporate() and isConsumer() methods.
    function setRetailInstanceService(service: RetailInstanceTypeService) {
        retailServiceInstance.value = service;
    }

    async function initialiseStripe() {
        const stripeConfiguration = await fetch("/api/stripe-conf").then((response) => response.json() as Promise<StripeConfiguration>);
        stripePublishableKey.value = stripeConfiguration.publishableKey;

        let additionalOpts: StripeConstructorOptions|undefined;
        if (stripeConfiguration.accountId) {
            additionalOpts = {
                stripeAccount: stripeConfiguration.accountId,
            };
            stripeAccountId.value = stripeConfiguration.accountId;
        }
        const stripePromise = loadStripe(stripeConfiguration.publishableKey, additionalOpts);
        stripePromise.then(() => {
            stripeReady.value = true;
        });
    }

    // This function is called after onSubmitPaymentButtonClick() has checked that any MI Questions are valid. Completes the payment process.
    // @param fulfilmentMethod - The delivery method selected by the user.
    // @param bookingId - The ID of the booking to be paid for.
    // @param stripeObject - The Stripe object containing necessary stripe data. Can be null if not using Stripe. That info has to be passed in from the StripeWidget component as it relies on things such as component refs which cannot be done in this store.
    function makePurchase(fulfilmentMethod: DeliveryMethods, bookingId: string, stripeObject: any) {
        // eslint-disable-next-line
        return new Promise<void>(async (resolve, reject) => {
            // Check if the service was set
            if (!retailServiceInstance.value) {
                handleError("Payment service is not properly configured (Retail Instance).");
                throw new Error("RetailInstanceTypeService not initialized in payment store.");
            }

            // Reset stripe error message
            stripePaymentErrorToDisplay.value = null;

            const bookingStore = useBookingData();
            const isUserCurrentlyLoggedIn = await isUserLoggedIn();

            if (retailServiceInstance.value.isCorporate() && !isUserCurrentlyLoggedIn) {
                // If the user is not logged in but they should be, we need to show an error message and stop the payment process.
                handleError("You must be logged in to make a payment.");
                reject();
                return;
            }

            isFinalPaymentStepLoading.value = true;

            // STEP 1 - Perform the Stripe payment to Stripe (if not an On-Acccount payment)
            if (!bookingStore.isOnAccountPaymentSelected()) {
                try {
                    const { error } = await stripeObject.stripeInstance.confirmPayment(stripeObject.confirmPaymentObject);
                    if (error !== undefined) {
                        stripePaymentErrorToDisplay.value = error;
                        isFinalPaymentStepLoading.value = false;
                        reject();
                        return;
                    }
                } catch (error) {
                    console.error("Error in makePurchase():", error);
                    isFinalPaymentStepLoading.value = false;
                    handleError(error);
                    reject();
                    return;
                }
            }

            try {
                // STEP 2 - Submit the payment to Rocket.
                const ip = await publicIpv4();

                // If there are any MI Questions, we need to pass them to the Rocket API, but re-format them to match the API's expected format.
                let miQuestionsForApi: SubmittedManagementQuestionAnswer[] = [];
                if (miQuestions.value.questionsAndAnswers.length > 0) {
                    miQuestionsForApi = Object.keys(miQuestions.value.questionsAndAnswers).map(
                        (key) =>
                            ({
                                key: miQuestions.value.questionsAndAnswers![key].id,
                                value: miQuestions.value.questionsAndAnswers![key].modelValue ?? "",
                            } as SubmittedManagementQuestionAnswer)
                    );
                }

                let bookingData: IBookingData | null = null;

                if (bookingStore.isOnAccountPaymentSelected()) {
                    // User has selected the On-Account payment method, so pass onAccount: true to the API.
                    bookingData = (await authenticatedCheckout(bookingId, fulfilmentMethod, ip, miQuestionsForApi, true, { onAccount: true })) as IBookingData;
                } else if (isUserCurrentlyLoggedIn) {
                    // User is logged in and not on account, so pass stripe: true to the API.
                    bookingData = (await authenticatedCheckout(bookingId, fulfilmentMethod, ip, miQuestionsForApi, true, { stripe: true })) as IBookingData;
                } else {
                    // User is not logged in and not on account, so is a guest user.
                    bookingData = (await guestCheckout(bookingId, guestUserEmail.value, guestUserFirstName.value, guestUserLastName.value, fulfilmentMethod, ip, true, { stripe: true })) as IBookingData;
                }

                // Send an event that can be picked up by Checkout.vue to show the sucess screen. Includes the latest booking data that is needed to show the success screen.
                emitter.emit("paymentCompletedSuccessfully", bookingData);

                bookingStore.clearBookingData();
            } catch (error) {
                console.error("Error in makePurchase():", error);
                handleError(error);
            } finally {
                isFinalPaymentStepLoading.value = false;
            }

            resolve();
        });
    }

    // Call this function before makePurchase() to check if the MI Questions are valid. Starts the payment process.
    // @param fulfilmentMethod - The delivery method selected by the user.
    // @param bookingId - The ID of the booking to be paid for.
    // @param stripeObject - The Stripe object containing necessary stripe data. Set to null if not using Stripe (i.e. On-Account payment).
    function onSubmitPaymentButtonClick(fulfilmentMethod: DeliveryMethods, bookingId: string, stripeObject: any) {
        if (miQuestions.value.questionsAndAnswers.length > 0) {
            // If there are miQuestions questions and answers, the MiQuestions component must exist, so send an event to it to check the MI Questions are valid and then it will call the next function to continue.
            emitter.emit("miQuestionsCheckAnswerValidity", { fulfilmentMethod, bookingId, stripeObject });
            return;
        }
        makePurchase(fulfilmentMethod, bookingId, stripeObject);
    }

    return {
        stripeReady,
        stripePublishableKey,
        stripeAccountId,
        initialiseStripe,
        miQuestions,
        onSubmitPaymentButtonClick,
        makePurchase,
        stripePaymentErrorToDisplay,
        isFinalPaymentStepLoading,
        setRetailInstanceService,
        guestUserEmail,
        guestUserFirstName,
        guestUserLastName,
    };
});
